import { cn } from '../../lib/cn';

export interface SkeletonProps {
  /** 'text': one line, 16px tall. 'rect': sized via width/height. 'circle': round. */
  variant?: 'text' | 'rect' | 'circle';
  width?: string | number;
  height?: string | number;
  className?: string;
}

/** Shimmer placeholder. Uses the global `.skeleton` class from index.css
 *  (cream-dark → cream-50 gradient, zeroed under prefers-reduced-motion). */
export default function Skeleton({ variant = 'text', width, height, className }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'skeleton',
        variant === 'text' && 'h-4 w-full',
        variant === 'circle' && 'rounded-full',
        className,
      )}
      style={{
        width: width ?? (variant === 'circle' ? height : undefined),
        height: height ?? (variant === 'circle' ? width : undefined),
      }}
    />
  );
}

export interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  density?: 'compact' | 'default';
  className?: string;
}

/** Table-shaped loading state: a header row plus `rows` body rows at real
 *  ledger row heights, so content doesn't jump when data lands. */
export function SkeletonTable({ rows = 8, columns = 4, density = 'default', className }: SkeletonTableProps) {
  return (
    <div role="status" aria-label="Loading" className={cn('w-full', className)}>
      <div className="flex items-center gap-4 border-b border-border px-3 py-2">
        {Array.from({ length: columns }, (_, c) => (
          <div key={c} className="flex-1">
            <Skeleton variant="rect" className="h-3" width={`${40 + ((c * 7) % 3) * 10}%`} />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          className={cn(
            'flex items-center gap-4 border-b border-border-subtle px-3',
            density === 'compact' ? 'h-8' : 'h-9',
          )}
        >
          {Array.from({ length: columns }, (_, c) => (
            <div key={c} className="flex-1">
              <Skeleton variant="rect" className="h-3" width={`${55 + ((r * 3 + c * 7) % 4) * 10}%`} />
            </div>
          ))}
        </div>
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

/** Spec-name alias (design-system-spec §2.9 exports `TableSkeleton`). */
export { SkeletonTable as TableSkeleton };
